import React, { useState, useEffect, useCallback } from 'react';
import { useStore } from '../../context/StoreContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from '../../components/ui/tabs';
import { 
  Plus, Trash, Ticket, Timer, Gift, Gear, Trophy,
  CircleNotch, Copy, CheckCircle, Truck
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ========== PROMO CODES ==========
const PromoCodesSection = ({ storeId }) => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    code: '', discount_type: 'percentage', discount_value: 10,
    min_order_amount: null, max_uses: null, max_uses_per_customer: 1,
    valid_from: '', valid_until: '', is_active: true
  });

  const fetchCodes = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/stores/${storeId}/promo-codes`, { withCredentials: true });
      setCodes(res.data);
    } catch { } finally { setLoading(false); }
  }, [storeId]);

  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  const handleCreate = async () => {
    if (!form.code) { toast.error('Code requis'); return; }
    try {
      await axios.post(`${API_URL}/api/stores/${storeId}/promo-codes`, form, { withCredentials: true });
      toast.success('Code promo créé');
      setOpen(false);
      setForm({ code: '', discount_type: 'percentage', discount_value: 10, min_order_amount: null, max_uses: null, max_uses_per_customer: 1, valid_from: '', valid_until: '', is_active: true });
      fetchCodes();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce code promo ?')) return;
    try {
      await axios.delete(`${API_URL}/api/stores/${storeId}/promo-codes/${id}`, { withCredentials: true });
      toast.success('Supprimé');
      fetchCodes();
    } catch { toast.error('Erreur'); }
  };

  const copyCode = (code) => { navigator.clipboard.writeText(code); toast.success('Code copié'); };

  return (
    <div className="space-y-4" data-testid="promo-codes-section">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold">Codes promo ({codes.length})</h3>
          <p className="text-sm text-slate-500">Créez des codes de réduction pour vos clients</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0052FF]" data-testid="new-promo-btn"><Plus className="mr-1" size={16} /> Nouveau code</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nouveau code promo</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Code *</Label>
                <Input value={form.code} onChange={e => setForm(p => ({ ...p, code: e.target.value.toUpperCase() }))} placeholder="EX: SOLDES20" data-testid="promo-code-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={form.discount_type} onValueChange={v => setForm(p => ({ ...p, discount_type: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Pourcentage (%)</SelectItem>
                      <SelectItem value="fixed">Montant fixe (EUR)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Valeur</Label>
                  <Input type="number" value={form.discount_value} onChange={e => setForm(p => ({ ...p, discount_value: parseFloat(e.target.value) || 0 }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Montant min. (EUR)</Label>
                  <Input type="number" value={form.min_order_amount || ''} onChange={e => setForm(p => ({ ...p, min_order_amount: e.target.value ? parseFloat(e.target.value) : null }))} placeholder="Aucun" />
                </div>
                <div>
                  <Label>Utilisations max.</Label>
                  <Input type="number" value={form.max_uses || ''} onChange={e => setForm(p => ({ ...p, max_uses: e.target.value ? parseInt(e.target.value) : null }))} placeholder="Illimité" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Valide à partir du</Label>
                  <Input type="date" value={form.valid_from} onChange={e => setForm(p => ({ ...p, valid_from: e.target.value }))} />
                </div>
                <div>
                  <Label>Valide jusqu'au</Label>
                  <Input type="date" value={form.valid_until} onChange={e => setForm(p => ({ ...p, valid_until: e.target.value }))} />
                </div>
              </div>
              <Button onClick={handleCreate} className="w-full bg-[#0052FF]" data-testid="create-promo-btn">Créer le code promo</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="py-8 text-center"><CircleNotch className="animate-spin mx-auto" size={24} /></div>
      ) : codes.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-slate-500">Aucun code promo</CardContent></Card>
      ) : (
        <Card><CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Réduction</TableHead>
                <TableHead>Min.</TableHead>
                <TableHead>Utilisations</TableHead>
                <TableHead>Validité</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {codes.map(c => (
                <TableRow key={c.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <code className="bg-slate-100 px-2 py-1 rounded text-sm font-mono font-bold">{c.code}</code>
                      <button onClick={() => copyCode(c.code)} className="text-slate-400 hover:text-slate-600"><Copy size={14} /></button>
                    </div>
                  </TableCell>
                  <TableCell className="font-semibold">{c.discount_type === 'percentage' ? `${c.discount_value}%` : `${c.discount_value} EUR`}</TableCell>
                  <TableCell>{c.min_order_amount ? `${c.min_order_amount} EUR` : '-'}</TableCell>
                  <TableCell>{c.uses_count || 0}{c.max_uses ? `/${c.max_uses}` : ''}</TableCell>
                  <TableCell className="text-xs">{c.valid_until || 'Illimité'}</TableCell>
                  <TableCell><Badge variant={c.is_active ? 'default' : 'secondary'}>{c.is_active ? 'Actif' : 'Inactif'}</Badge></TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(c.id)}><Trash size={16} className="text-red-500" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent></Card>
      )}
    </div>
  );
};

// ========== LIMITED OFFERS ==========
const LimitedOffersSection = ({ storeId }) => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [form, setForm] = useState({
    name: '', discount_type: 'percentage', discount_value: 20, sale_price: null,
    max_orders: null, end_date: '', product_ids: [], is_active: true
  });

  const fetchOffers = useCallback(async () => {
    try { const res = await axios.get(`${API_URL}/api/stores/${storeId}/limited-offers`, { withCredentials: true }); setOffers(res.data); }
    catch { } finally { setLoading(false); }
  }, [storeId]);

  useEffect(() => { fetchOffers(); }, [fetchOffers]);

  // Fetch products for selector
  useEffect(() => {
    if (!open) return;
    axios.get(`${API_URL}/api/stores/${storeId}/products?limit=500`, { withCredentials: true })
      .then(r => {
        const items = r.data?.products || r.data || [];
        setProducts(items);
      }).catch(() => {});
  }, [open, storeId]);

  const filteredProducts = productSearch
    ? products.filter(p => p.name?.toLowerCase().includes(productSearch.toLowerCase()))
    : products.slice(0, 20);

  const toggleProduct = (pid) => {
    setForm(prev => ({
      ...prev,
      product_ids: prev.product_ids.includes(pid)
        ? prev.product_ids.filter(id => id !== pid)
        : [...prev.product_ids, pid]
    }));
  };

  const resetForm = () => {
    setForm({ name: '', discount_type: 'percentage', discount_value: 20, sale_price: null, max_orders: null, end_date: '', product_ids: [], is_active: true });
    setProductSearch('');
  };

  const handleCreate = async () => {
    if (!form.name) { toast.error('Nom requis'); return; }
    if (form.product_ids.length === 0) { toast.error('Sélectionnez au moins un produit'); return; }
    try {
      await axios.post(`${API_URL}/api/stores/${storeId}/limited-offers`, form, { withCredentials: true });
      toast.success('Offre créée'); setOpen(false); resetForm(); fetchOffers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (offerId) => {
    if (!window.confirm('Supprimer cette offre ?')) return;
    try {
      await axios.delete(`${API_URL}/api/stores/${storeId}/limited-offers/${offerId}`, { withCredentials: true });
      toast.success('Offre supprimée'); fetchOffers();
    } catch { toast.error('Erreur'); }
  };

  const handleToggleActive = async (offer) => {
    try {
      await axios.put(`${API_URL}/api/stores/${storeId}/limited-offers/${offer.id}`, {
        ...offer, is_active: !offer.is_active, products: undefined
      }, { withCredentials: true });
      toast.success(offer.is_active ? 'Offre désactivée' : 'Offre activée'); fetchOffers();
    } catch { toast.error('Erreur'); }
  };

  return (
    <div className="space-y-4" data-testid="limited-offers-section">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold">Offres limitées ({offers.length})</h3>
          <p className="text-sm text-slate-500">Créez des offres avec prix remisé, limitées dans le temps ou en nombre de commandes</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
          <DialogTrigger asChild><Button className="bg-amber-600 hover:bg-amber-700" data-testid="new-offer-btn"><Plus className="mr-1" size={16} /> Nouvelle offre</Button></DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Nouvelle offre limitée</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Nom de l'offre *</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Ex: Flash Sale Printemps" data-testid="offer-name" /></div>

              {/* Product Selector */}
              <div>
                <Label>Produits concernés *</Label>
                <Input placeholder="Rechercher un produit..." value={productSearch} onChange={e => setProductSearch(e.target.value)} className="mt-1 mb-2" data-testid="offer-product-search" />
                <div className="border rounded-lg max-h-[200px] overflow-y-auto">
                  {filteredProducts.length === 0 ? (
                    <p className="p-3 text-sm text-slate-400 text-center">Aucun produit trouvé</p>
                  ) : filteredProducts.map(p => (
                    <label key={p.id} className={`flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer border-b last:border-b-0 ${form.product_ids.includes(p.id) ? 'bg-amber-50' : ''}`}>
                      <input type="checkbox" checked={form.product_ids.includes(p.id)} onChange={() => toggleProduct(p.id)} className="rounded" />
                      {(p.featured_image || p.images?.[0]) && <img src={p.featured_image || p.images[0]} alt="" className="w-8 h-8 rounded object-cover" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{p.name}</p>
                        <p className="text-xs text-slate-400">{parseFloat(p.price).toFixed(2)} EUR</p>
                      </div>
                    </label>
                  ))}
                </div>
                {form.product_ids.length > 0 && (
                  <p className="text-xs text-amber-600 mt-1 font-medium">{form.product_ids.length} produit(s) sélectionné(s)</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type de remise</Label>
                  <Select value={form.discount_type} onValueChange={v => setForm(p => ({ ...p, discount_type: v }))}>
                    <SelectTrigger data-testid="offer-discount-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Pourcentage (%)</SelectItem>
                      <SelectItem value="fixed">Montant fixe (EUR)</SelectItem>
                      <SelectItem value="sale_price">Prix remisé direct</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.discount_type === 'sale_price' ? (
                  <div><Label>Prix remisé (EUR)</Label><Input type="number" step="0.01" value={form.sale_price || ''} onChange={e => setForm(p => ({ ...p, sale_price: e.target.value ? parseFloat(e.target.value) : null }))} placeholder="Ex: 19.90" data-testid="offer-sale-price" /></div>
                ) : (
                  <div><Label>Valeur de la remise</Label><Input type="number" value={form.discount_value} onChange={e => setForm(p => ({ ...p, discount_value: parseFloat(e.target.value) || 0 }))} data-testid="offer-discount-value" /></div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div><Label>Max commandes</Label><Input type="number" value={form.max_orders || ''} onChange={e => setForm(p => ({ ...p, max_orders: e.target.value ? parseInt(e.target.value) : null }))} placeholder="Illimité" data-testid="offer-max-orders" /></div>
                <div><Label>Date de fin</Label><Input type="datetime-local" value={form.end_date} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))} data-testid="offer-end-date" /></div>
              </div>

              <Button onClick={handleCreate} className="w-full bg-amber-600 hover:bg-amber-700" data-testid="create-offer-btn">Créer l'offre</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? <div className="py-8 text-center"><CircleNotch className="animate-spin mx-auto" size={24} /></div> :
      offers.length === 0 ? <Card><CardContent className="py-8 text-center text-slate-500">Aucune offre limitée</CardContent></Card> :
      <div className="space-y-3">
        {offers.map(o => (
          <Card key={o.id}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold">{o.name}</h4>
                    <Badge variant={o.is_active ? 'default' : 'secondary'} className="text-[10px]">{o.is_active ? 'Active' : 'Inactive'}</Badge>
                  </div>
                  <p className="text-2xl font-bold text-amber-600">
                    {o.discount_type === 'percentage' ? `−${o.discount_value}%` : o.discount_type === 'sale_price' ? `${o.sale_price?.toFixed(2)} EUR` : `−${o.discount_value} EUR`}
                  </p>
                  {/* Show linked products */}
                  {o.products && o.products.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {o.products.map(p => (
                        <span key={p.id} className="inline-flex items-center gap-1 text-[11px] bg-slate-100 rounded-full px-2 py-0.5 text-slate-600">
                          {p.name?.length > 30 ? p.name.slice(0, 28) + '...' : p.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={o.is_active} onCheckedChange={() => handleToggleActive(o)} />
                  <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-600" onClick={() => handleDelete(o.id)} data-testid={`delete-offer-${o.id}`}>
                    <Trash size={16} />
                  </Button>
                </div>
              </div>
              <div className="flex gap-4 mt-3 text-xs text-slate-500">
                {(o.max_orders || o.max_customers) && <span><Trophy size={12} className="inline mr-1" />{o.customers_used || 0}/{o.max_orders || o.max_customers} commandes</span>}
                {o.end_date && <span><Timer size={12} className="inline mr-1" />Expire: {new Date(o.end_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>}
                {o.product_ids?.length > 0 && <span>{o.product_ids.length} produit(s)</span>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>}
    </div>
  )
};

// ========== FREE SHIPPING CONFIG ==========
const FreeShippingSection = ({ storeId, store }) => {
  const [config, setConfig] = useState({ enabled: false, threshold: 50, message_below: 'Plus que {remaining} EUR pour la livraison offerte !', message_reached: 'Livraison offerte !' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (store?.settings?.free_shipping) setConfig(store.settings.free_shipping);
  }, [store]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/stores/${storeId}/free-shipping`, config, { withCredentials: true });
      toast.success('Configuration sauvegardée');
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-xl border p-5 space-y-4" data-testid="free-shipping-config">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold">Livraison offerte</h3>
          <p className="text-sm text-slate-500">Barre de progression sur la fiche produit et le panier</p>
        </div>
        <Switch checked={config.enabled} onCheckedChange={v => setConfig(p => ({ ...p, enabled: v }))} />
      </div>
      {config.enabled && (
        <div className="space-y-3 pt-3 border-t">
          <div>
            <Label>Seuil de livraison offerte (EUR)</Label>
            <Input type="number" step="0.01" value={config.threshold} onChange={e => setConfig(p => ({ ...p, threshold: parseFloat(e.target.value) || 0 }))} />
          </div>
          <div>
            <Label>Message avant le seuil</Label>
            <Input value={config.message_below} onChange={e => setConfig(p => ({ ...p, message_below: e.target.value }))} />
            <p className="text-xs text-slate-500 mt-1">Utilisez {'{remaining}'} pour le montant restant</p>
          </div>
          <div>
            <Label>Message seuil atteint</Label>
            <Input value={config.message_reached} onChange={e => setConfig(p => ({ ...p, message_reached: e.target.value }))} />
          </div>
          <Button onClick={save} disabled={saving} className="bg-[#0052FF]">
            {saving ? <CircleNotch className="animate-spin mr-2" size={14} /> : <CheckCircle className="mr-2" size={14} />}
            Sauvegarder
          </Button>
        </div>
      )}
    </div>
  );
};

// ========== LOYALTY CONFIG ==========
const LoyaltySection = ({ storeId, store }) => {
  const [config, setConfig] = useState({ enabled: false, points_per_euro: 1, points_value_euros: 0.01, min_points_redeem: 100 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (store?.loyalty_config) setConfig(store.loyalty_config);
  }, [store]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/stores/${storeId}/loyalty`, config, { withCredentials: true });
      toast.success('Configuration fidélité sauvegardée');
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-xl border p-5 space-y-4" data-testid="loyalty-config">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold">Programme de fidélité</h3>
          <p className="text-sm text-slate-500">1 EUR = X points, Y points = Z EUR de remise</p>
        </div>
        <Switch checked={config.enabled} onCheckedChange={v => setConfig(p => ({ ...p, enabled: v }))} />
      </div>
      {config.enabled && (
        <div className="space-y-3 pt-3 border-t">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Points / EUR dépensé</Label>
              <Input type="number" step="0.1" value={config.points_per_euro} onChange={e => setConfig(p => ({ ...p, points_per_euro: parseFloat(e.target.value) || 0 }))} />
            </div>
            <div>
              <Label>Valeur EUR / point</Label>
              <Input type="number" step="0.001" value={config.points_value_euros} onChange={e => setConfig(p => ({ ...p, points_value_euros: parseFloat(e.target.value) || 0 }))} />
            </div>
            <div>
              <Label>Min. pour utiliser</Label>
              <Input type="number" value={config.min_points_redeem} onChange={e => setConfig(p => ({ ...p, min_points_redeem: parseInt(e.target.value) || 0 }))} />
            </div>
          </div>
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
            <strong>Exemple :</strong> Un client qui dépense 100 EUR gagne {(100 * config.points_per_euro).toFixed(0)} points, soit {(100 * config.points_per_euro * config.points_value_euros).toFixed(2)} EUR de remise.
          </div>
          <Button onClick={save} disabled={saving} className="bg-[#0052FF]">
            {saving ? <CircleNotch className="animate-spin mr-2" size={14} /> : <CheckCircle className="mr-2" size={14} />}
            Sauvegarder
          </Button>
        </div>
      )}
    </div>
  );
};

// ========== SMTP CONFIG ==========
const SmtpSection = ({ storeId, store }) => {
  const [config, setConfig] = useState({ host: '', port: 465, username: '', password: '', use_ssl: true, from_email: '', from_name: '' });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (store?.smtp_config) setConfig(store.smtp_config);
  }, [store]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/stores/${storeId}/smtp`, config, { withCredentials: true });
      toast.success('SMTP configuré');
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const testSmtp = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API_URL}/api/stores/${storeId}/smtp/test`, {}, { withCredentials: true });
      toast.success(res.data.message);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur SMTP'); }
    finally { setTesting(false); }
  };

  return (
    <div className="bg-white rounded-xl border p-5 space-y-4" data-testid="smtp-config">
      <h3 className="font-semibold">Configuration SMTP (Emails)</h3>
      <p className="text-sm text-slate-500">Configurez votre serveur email pour personnaliser les emails envoyés (confirmations, etc.)</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Serveur SMTP</Label>
          <Input value={config.host} onChange={e => setConfig(p => ({ ...p, host: e.target.value }))} placeholder="ssl0.ovh.net" />
        </div>
        <div>
          <Label>Port</Label>
          <Input type="number" value={config.port} onChange={e => setConfig(p => ({ ...p, port: parseInt(e.target.value) || 465 }))} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Utilisateur</Label>
          <Input value={config.username} onChange={e => setConfig(p => ({ ...p, username: e.target.value }))} placeholder="contact@ma-boutique.fr" />
        </div>
        <div>
          <Label>Mot de passe</Label>
          <Input type="password" value={config.password} onChange={e => setConfig(p => ({ ...p, password: e.target.value }))} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Email expéditeur</Label>
          <Input value={config.from_email} onChange={e => setConfig(p => ({ ...p, from_email: e.target.value }))} placeholder="contact@ma-boutique.fr" />
        </div>
        <div>
          <Label>Nom expéditeur</Label>
          <Input value={config.from_name} onChange={e => setConfig(p => ({ ...p, from_name: e.target.value }))} placeholder="Ma Boutique" />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Switch checked={config.use_ssl} onCheckedChange={v => setConfig(p => ({ ...p, use_ssl: v }))} />
        <Label>SSL/TLS (port 465)</Label>
      </div>
      <div className="flex gap-3">
        <Button onClick={save} disabled={saving} className="bg-[#0052FF]">
          {saving ? <CircleNotch className="animate-spin mr-2" size={14} /> : null}
          Sauvegarder
        </Button>
        <Button onClick={testSmtp} disabled={testing} variant="outline">
          {testing ? <CircleNotch className="animate-spin mr-2" size={14} /> : null}
          Envoyer un test
        </Button>
      </div>
    </div>
  );
};

// ========== MAIN PAGE ==========
const MarketingPage = () => {
  const { currentStore } = useStore();
  const [store, setStore] = useState(null);

  useEffect(() => {
    if (!currentStore) return;
    axios.get(`${API_URL}/api/stores/${currentStore.id}`, { withCredentials: true })
      .then(r => setStore(r.data))
      .catch(() => {});
  }, [currentStore]);

  if (!currentStore) return <div className="py-12 text-center text-slate-500">Sélectionnez une boutique</div>;

  return (
    <div data-testid="marketing-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Marketing & Promotions</h1>
        <p className="text-sm text-slate-500 mt-1">Gérez les codes promo, offres limitées, fidélité et emails</p>
      </div>

      <Tabs defaultValue="promos" className="w-full">
        <TabsList className="bg-slate-100 p-1 rounded-lg mb-6 flex-wrap h-auto gap-1">
          <TabsTrigger value="promos" className="text-xs data-[state=active]:bg-white"><Ticket className="mr-1" size={14} />Codes promo</TabsTrigger>
          <TabsTrigger value="offers" className="text-xs data-[state=active]:bg-white"><Timer className="mr-1" size={14} />Offres limitées</TabsTrigger>
          <TabsTrigger value="shipping" className="text-xs data-[state=active]:bg-white"><Truck className="mr-1" size={14} />Livraison offerte</TabsTrigger>
          <TabsTrigger value="loyalty" className="text-xs data-[state=active]:bg-white"><Gift className="mr-1" size={14} />Fidélité</TabsTrigger>
          <TabsTrigger value="smtp" className="text-xs data-[state=active]:bg-white"><Gear className="mr-1" size={14} />Emails / SMTP</TabsTrigger>
        </TabsList>

        <TabsContent value="promos"><PromoCodesSection storeId={currentStore.id} /></TabsContent>
        <TabsContent value="offers"><LimitedOffersSection storeId={currentStore.id} /></TabsContent>
        <TabsContent value="shipping"><FreeShippingSection storeId={currentStore.id} store={store} /></TabsContent>
        <TabsContent value="loyalty"><LoyaltySection storeId={currentStore.id} store={store} /></TabsContent>
        <TabsContent value="smtp"><SmtpSection storeId={currentStore.id} store={store} /></TabsContent>
      </Tabs>
    </div>
  );
};

export default MarketingPage;
